'use client';

import {
	Corner,
	Root,
	Scrollbar,
	Thumb,
	Viewport,
} from '@radix-ui/react-scroll-area';
import { IconChevronRight, IconHash, IconSearch } from '@tabler/icons-react';
import { Link } from 'next-view-transitions';
import { cn } from '@/lib/utils';
import { Article, CategoryWithCount } from '@/types/types';
import { BlogSidebarItem } from './blog-sidebar-item';

interface BrowseCategoriesProps {
	categories: CategoryWithCount[];
	article?: Article;
	searchQuery: string;
	setSearchQuery: (query: string) => void;
	isExpanded: boolean;
	setIsExpanded: (isExpanded: boolean) => void;
}

export function BrowseCategories({
	categories,
	article,
	searchQuery,
	setSearchQuery,
	isExpanded,
	setIsExpanded,
}: BrowseCategoriesProps) {
	// Filter categories based on search query
	const filteredCategories = categories.filter(
		category =>
			category.name?.toLowerCase().includes(searchQuery.toLowerCase()) ??
			false,
	);

	const renderFooter = () => {
		if (filteredCategories.length <= 5) return null;

		return (
			<div className="w-full text-center">
				<button
					onClick={() => setIsExpanded(!isExpanded)}
					className="text-xs font-medium text-primary hover:text-primary/80">
					{isExpanded ? 'Show less' : 'Show more'}
				</button>
			</div>
		);
	};

	return (
		<BlogSidebarItem
			icon={<IconHash size={18} />}
			title="Browse Categories"
			contentClassName="p-0"
			footer={renderFooter()}>
			<div className="px-4 py-3">
				<div className="relative">
					<input
						type="text"
						placeholder="Search categories..."
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						className="w-full rounded-md border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm placeholder-neutral-400 focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
					/>
					<IconSearch
						size={16}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
					/>
				</div>
			</div>
			<Root className="w-full" type="auto">
				<Viewport
					className={cn(
						'w-full',
						isExpanded
							? 'max-h-[calc(100vh-16rem)]'
							: 'max-h-[300px]',
					)}>
					<div className="divide-y divide-neutral-100">
						{filteredCategories.length > 0 ? (
							filteredCategories.map(category => (
								<Link
									key={category.id}
									href={`/blog?category=${category.slug}`}
									className={cn(
										'group flex items-center justify-between px-6 py-3.5 transition-all duration-200',
										article?.categories?.some(
											cat => cat.id === category.id,
										)
											? 'bg-primary/5 hover:bg-primary/10'
											: 'hover:bg-neutral-50',
									)}>
									<span
										className={cn(
											'text-sm transition-colors',
											article?.categories?.some(
												cat => cat.id === category.id,
											)
												? 'font-medium text-primary'
												: 'text-neutral-700 group-hover:text-neutral-900',
										)}>
										{category.name || 'Unnamed'}
									</span>
									<div className="flex items-center gap-3">
										<span
											className={cn(
												'rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
												article?.categories?.some(
													cat =>
														cat.id === category.id,
												)
													? 'bg-primary/10 text-primary'
													: 'bg-neutral-100 text-neutral-600 group-hover:bg-neutral-200',
											)}>
											{category.articles_count || 0}
										</span>
										<IconChevronRight
											size={16}
											className={cn(
												'transform transition-transform group-hover:translate-x-0.5',
												article?.categories?.some(
													cat =>
														cat.id === category.id,
												)
													? 'text-primary'
													: 'text-neutral-400',
											)}
										/>
									</div>
								</Link>
							))
						) : (
							<div className="flex flex-col items-center justify-center py-8 text-center">
								<IconSearch
									size={24}
									className="mb-2 text-neutral-300"
								/>
								<p className="text-sm text-neutral-500">
									No categories found
								</p>
								<button
									onClick={() => setSearchQuery('')}
									className="mt-2 text-xs font-medium text-primary hover:text-primary/80">
									Clear search
								</button>
							</div>
						)}
					</div>
				</Viewport>
				<Scrollbar
					className="flex touch-none select-none p-0.5 transition-colors duration-150 ease-out hover:bg-neutral-100 data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col"
					orientation="vertical">
					<Thumb className="relative flex-1 rounded-[10px] bg-neutral-300 before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']" />
				</Scrollbar>
				<Corner className="bg-neutral-200" />
			</Root>
		</BlogSidebarItem>
	);
}
